import { useTranslation } from "react-i18next";
import { useDeferredValue, useMemo, useState } from "react";
import { ChevronRight, Layers, List, Star } from "lucide-react";
import { Button as AriaButton } from "react-aria-components";
import { Button } from "@/components/ui/button";
import { Disclosure } from "@/components/ui/disclosure";
import { cn } from "@/lib/utils";
import type { AuthSession } from "../../../auth/keycloak.js";
import { useFacilities } from "../../facilities/useFacilities.js";
import { useFacilityFavourites } from "../../facilities/useFacilityFavourites.js";
import { useFacilityNavParams } from "../../facilities/drilldown/useFacilityNavParams.js";
import { FilterField } from "../../roles/FilterField.js";
import { favouriteFacilityCards, filterCards, groupByParent, type FavouriteFacilityCard } from "./favourite-facilities-model.js";

/**
 * The legacy Favoriten-Bereiche page (ADR-034 D7): the caller's favourite facilities as cards — breadcrumb, name,
 * "N categories · M manuals", the star (persisted, optimistic) — grouped by parent path (toggle) with a name filter.
 * A card opens the facility's manuals; an ancestor the caller reaches only through descendants (`permission: null`)
 * DRILLS IN instead (plan panel #4 — both navigators drop such an `arc_facility` as stale).
 */
export function FavouriteFacilitiesPage({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const nav = useFacilityNavParams();
  const facilities = useFacilities(session);
  const favs = useFacilityFavourites(session);
  const [term, setTerm] = useState("");
  const [grouped, setGrouped] = useState(true);
  const deferredTerm = useDeferredValue(term);
  const cards = useMemo(() => favouriteFacilityCards(facilities.data ?? [], favs.favourites), [facilities.data, favs.favourites]);
  const shown = useMemo(() => filterCards(cards, deferredTerm), [cards, deferredTerm]);
  const groups = useMemo(() => (grouped ? groupByParent(shown, t("dashboard.topLevel")) : null), [grouped, shown]);
  const open = (c: FavouriteFacilityCard) => (c.permission === null ? nav.drillTo(c.id) : nav.openManuals(c.id));

  const card = (c: FavouriteFacilityCard, showParent: boolean) => (
    <li key={c.id} className="border-border bg-background flex items-center gap-2 rounded-lg border p-3">
      <AriaButton onPress={() => open(c)} className="focus-visible:ring-ring/50 flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded border-0 bg-transparent p-0 text-left outline-none focus-visible:ring-2">
        <span className="min-w-0 flex-1">
          {showParent && c.parentLabel !== "" ? <span className="text-muted-foreground block truncate text-xs">{c.parentLabel}</span> : null}
          <span className="block truncate text-sm font-medium">{c.name}</span>
          {c.permission !== null && c.categoryCount !== null && c.manualCount !== null ? (
            <span className="text-muted-foreground block text-xs">
              {t("dashboard.countCategories", { count: c.categoryCount })} · {t("dashboard.countManuals", { count: c.manualCount })}
            </span>
          ) : c.childCount > 0 ? (
            <span className="text-muted-foreground block text-xs">
              {t("dashboard.countSubfacilities", { count: c.childCount })}
            </span>
          ) : null}
        </span>
        <ChevronRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
      </AriaButton>
      <AriaButton
        aria-label={t("dashboard.unfavoriteName", { name: c.name })}
        aria-pressed
        onPress={() => favs.toggle(c.id)}
        className="text-attention hover:bg-muted focus-visible:ring-ring/50 cursor-pointer rounded-full border-0 bg-transparent p-1.5 outline-none focus-visible:ring-2"
      >
        <Star aria-hidden="true" className="size-4 fill-current" />
      </AriaButton>
    </li>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <FilterField label={t("dashboard.filterFavouriteFacilitiesByName")} placeholder={t("dashboard.filterByName")} value={term} onChange={setTerm} className="w-full sm:w-64" />
        <Button variant="outline" size="icon-sm" aria-label={t("dashboard.groupByParentFacility")} aria-pressed={grouped} onPress={() => setGrouped((g) => !g)}>
          {grouped ? <Layers aria-hidden="true" className="size-4" /> : <List aria-hidden="true" className="size-4" />}
        </Button>
      </div>
      {facilities.isPending || favs.isPending ? (
        <p role="status" className="text-muted-foreground text-sm">
          {t("other.loading")}
        </p>
      ) : facilities.isError || favs.isError ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-destructive text-sm">
            {t("dashboard.couldntLoadYourFavouriteFacilities")}
          </p>
          <Button
            variant="outline"
            size="sm"
            onPress={() => {
              if (facilities.isError) void facilities.refetch();
              if (favs.isError) favs.refetch();
            }}
          >
            {t("dashboard.tryAgain")}
          </Button>
        </div>
      ) : cards.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("dashboard.noFavouriteFacilitiesYet")}</p>
      ) : shown.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("dashboard.noFavouriteFacilityMatches", { query: term.trim() })}</p>
      ) : groups !== null ? (
        <div className="flex flex-col gap-2">
          {groups.map((g) => (
            <Disclosure key={g.label} defaultExpanded title={<span className="truncate">{g.label}</span>}>
              <ul aria-label={t("dashboard.favouriteFacilitiesInLabel", { label: g.label })} className={cn("m-0 flex list-none flex-col gap-2 p-0")}>
                {g.cards.map((c) => card(c, false))}
              </ul>
            </Disclosure>
          ))}
        </div>
      ) : (
        <ul aria-label={t("dashboard.favouriteFacilities")} className="m-0 flex list-none flex-col gap-2 p-0">
          {shown.map((c) => card(c, true))}
        </ul>
      )}
    </div>
  );
}
